import { fib4CalculatorKeys as k } from "@/i18n/fib4-calculator-keys"

/**
 * FIB-4: a non-invasive index of the likelihood that a liver already has
 * advanced fibrosis, built from four values on a routine blood report.
 *
 *   FIB-4 = (age x AST) / (platelets x sqrt(ALT))
 *
 * Source: Sterling RK et al., "Development of a simple noninvasive index to
 * predict significant fibrosis in patients with HIV/HCV coinfection",
 * Hepatology 2006;43:1317-1325. The age-adjusted lower cut-off applied from 65
 * comes from McPherson S et al., "Age as a confounding factor for the accurate
 * non-invasive diagnosis of advanced NAFLD fibrosis", Am J Gastroenterol
 * 2017;112:740-751.
 *
 * Unlike SCORE2 (lib/score2.ts) there is nothing per-country here. FIB-4 is
 * four numbers and a square root, with no fitted coefficients and no regional
 * recalibration, so there is no market map to keep in step with the gate and
 * no way for a reader in the wrong market to be scored on the wrong scale. The
 * page is CH-only because of where its copy exists, not because of the model.
 *
 * Pure data plus arithmetic, and in `lib/` rather than beside the route so the
 * dependency points the way the rest of the tree does (app -> lib, never
 * back): the widget, the on-page threshold table AND the markdown mirror in
 * `lib/native-markdown/fib4-calculator.ts` read this one source, so a changed
 * cut-off cannot drift between them. It is also unit-testable without
 * rendering React.
 */

/**
 * The ages this calculator will score.
 *
 * A plausibility range, not a published bound: FIB-4 carries no age term
 * beyond the multiplication, so the formula returns a number for a 6-year-old
 * exactly as readily as for a 60-year-old. The index was developed and
 * validated in adults, and paediatric liver disease is assessed with different
 * tools entirely, so under 18 the widget declines rather than printing one.
 */
export const FIB4_MIN_AGE = 18
export const FIB4_MAX_AGE = 100

/**
 * Below this age the score is reported with a caveat rather than refused.
 *
 * Age multiplies the whole numerator, so a young adult's FIB-4 comes out low
 * almost regardless of the enzymes and platelets behind it, and a low result
 * there rules out much less than the same result at 50. The AGA/AASLD fatty
 * liver care pathway makes the same point in both directions; the upper half
 * is handled properly, by the age-adjusted cut-off in `AGE_GROUP_DEFS`, so
 * this constant covers only the lower one.
 *
 * A note and not a refusal on purpose: the score IS the published score at 30,
 * and telling a 30-year-old their result cannot be computed would be wrong.
 * What they need is to know how little a low one proves.
 */
export const FIB4_REDUCED_ACCURACY_MAX_AGE = 35

/**
 * Plausibility bounds on the three measured inputs. Outside these a value is
 * almost certainly a typo, the wrong unit or an acute illness the index is not
 * calibrated for.
 *
 * Units are the ones Swiss laboratories report: U/L for both aminotransferases
 * and G/L (10^9 per litre) for the platelet count. There is no unit switcher,
 * for the reason the SCORE2 page has no mg/dL field: a converter is the one
 * place a reader can feed a number into a term expecting a different scale and
 * get a wrong answer that still looks plausible.
 */
export const MIN_AST = 5
export const MAX_AST = 1000
export const MIN_ALT = 5
export const MAX_ALT = 1000
export const MIN_PLATELETS = 10
export const MAX_PLATELETS = 1000

export interface Fib4Input {
  /** Years. */
  age: number
  /** Aspartate aminotransferase (ASAT / GOT), U/L. */
  ast: number
  /** Alanine aminotransferase (ALAT / GPT), U/L. */
  alt: number
  /** Platelet count, G/L, i.e. 10^9 per litre. */
  platelets: number
}

/**
 * The published index.
 *
 * Both transaminases are in the formula but they pull in opposite directions:
 * AST multiplies, ALT divides through a square root. That is not a quirk of
 * the arithmetic, it is what the pair is measuring. In a healthy liver ALT
 * runs at or above AST; as fibrosis advances the ratio inverts, and the score
 * is built to notice that inversion rather than the absolute height of either
 * enzyme.
 *
 * Returns the unrounded index; callers display and classify `roundScore()` of
 * it, never this value directly (see `roundScore`).
 */
export function fib4Score({ age, ast, alt, platelets }: Fib4Input): number {
  return (age * ast) / (platelets * Math.sqrt(alt))
}

/**
 * The score as it is shown to the reader: two decimal places, which is how the
 * literature and every clinical calculator print it.
 *
 * Callers must classify THIS value, not the raw one. The cut-offs are
 * themselves quoted at two decimals (1.30, 2.00, 2.67), so classifying the raw
 * value and displaying the rounded one lets the two disagree at a band edge: a
 * raw 2.6712 displays as "2.67" but classifies as high risk, so a reader would
 * see "2.67" next to a band the page's own table puts at "above 2.67".
 * Rounding first means the label always describes the number on screen.
 */
export function roundScore(value: number): number {
  return Math.round(value * 100) / 100
}

/** Why an otherwise complete set of inputs cannot be scored. */
export type Fib4Problem = "ageOutOfRange" | "plateletsUnit" | "outOfRange"

/**
 * The first reason `input` cannot be scored, or null when it can.
 *
 * The order is the order of the messages' usefulness, not the order of the
 * fields:
 *
 *   - Age first, because it is the one bound with a real answer behind it. A
 *     12-year-old has not mistyped anything.
 *   - Then a platelet count above the accepted maximum, which gets its own
 *     message. It is the single most likely mistake on this page: the same
 *     count is written 250 in Switzerland and 250,000 in the American
 *     convention, and "check your entries" would send a reader looking for a
 *     typo that is not there. Left to the generic bounds it would also be the
 *     most dangerous one to wave through, since a thousandfold-too-large
 *     denominator produces a reassuringly small score.
 *   - Then the plain plausibility bounds.
 */
export function fib4Problem(input: Fib4Input): Fib4Problem | null {
  if (input.age < FIB4_MIN_AGE || input.age > FIB4_MAX_AGE) {
    return "ageOutOfRange"
  }
  if (input.platelets > MAX_PLATELETS) {
    return "plateletsUnit"
  }
  if (
    input.ast < MIN_AST ||
    input.ast > MAX_AST ||
    input.alt < MIN_ALT ||
    input.alt > MAX_ALT ||
    input.platelets < MIN_PLATELETS
  ) {
    return "outOfRange"
  }
  return null
}

/**
 * Whether a scoreable age still needs the young-adult caveat beside the
 * result. See `FIB4_REDUCED_ACCURACY_MAX_AGE`.
 */
export function fib4HasReducedAccuracy(age: number): boolean {
  return age < FIB4_REDUCED_ACCURACY_MAX_AGE
}

// ─── Result bands and the age groups their cut-offs are split by ────────────

export type Fib4Band = Fib4BandDef["id"]

/**
 * The three FIB-4 result bands, low to high. `fib4BandFor` walks this in
 * order.
 *
 * The middle one is not a hedge, it is the published finding: FIB-4 has a good
 * negative predictive value below its lower cut-off and a usable positive one
 * above the upper, and between them it has not decided. Collapsing it into two
 * bands would turn "needs a second test" into a verdict.
 *
 * `as const` is load-bearing here for the same reason it is in the BMI
 * calculator's `bmi.ts`: annotating the `*Key` fields as
 * `MessageKeys<"fib4Calculator">` would widen them to the namespace's whole key
 * union and make next-intl's typed `t()` demand a `values` argument at every
 * `t(band.labelKey)` call site.
 */
const BAND_DEFS = [
  { id: "low", labelKey: k.bandLow, meaningKey: k.meaningLow },
  {
    id: "indeterminate",
    labelKey: k.bandIndeterminate,
    meaningKey: k.meaningIndeterminate,
  },
  { id: "high", labelKey: k.bandHigh, meaningKey: k.meaningHigh },
] as const

export type Fib4BandDef = (typeof BAND_DEFS)[number]

export const BAND_ORDER: readonly Fib4BandDef[] = BAND_DEFS

export const FIB4_BANDS: Record<Fib4Band, Fib4BandDef> = Object.fromEntries(
  BAND_DEFS.map((b) => [b.id, b])
) as Record<Fib4Band, Fib4BandDef>

export type Fib4AgeGroup = Fib4AgeGroupDef["id"]

/**
 * The two cut-off sets, split at 65.
 *
 * Only the LOWER cut-off moves, from 1.30 to 2.00. Age sits in the numerator,
 * so the same liver scores higher with every passing year, and applying 1.30
 * from 65 onwards refers a large number of people with healthy livers for a
 * scan. The upper cut-off is unchanged at 2.67 in both groups, because it is
 * the lower one that does the ruling out and therefore the one whose
 * specificity collapses with age.
 *
 * Both groups are reachable through the widget, unlike the SCORE2 table's
 * third row: this calculator scores every adult age it accepts.
 */
const AGE_GROUP_DEFS = [
  {
    id: "under65",
    /** Exclusive upper bound; `Infinity` closes the walk. */
    maxAge: 65,
    labelKey: k.ageUnder65,
    lowBelow: 1.3,
    highAbove: 2.67,
    thresholdKeys: [
      k.thrUnder65Low,
      k.thrUnder65Indeterminate,
      k.thrUnder65High,
    ],
  },
  {
    id: "from65",
    maxAge: Infinity,
    labelKey: k.age65plus,
    lowBelow: 2,
    highAbove: 2.67,
    thresholdKeys: [k.thr65plusLow, k.thr65plusIndeterminate, k.thr65plusHigh],
  },
] as const

export type Fib4AgeGroupDef = (typeof AGE_GROUP_DEFS)[number]

export const AGE_GROUP_ORDER: readonly Fib4AgeGroupDef[] = AGE_GROUP_DEFS

/** Which cut-off set an age falls into. */
export function fib4AgeGroupFor(age: number): Fib4AgeGroupDef {
  for (const group of AGE_GROUP_ORDER) {
    if (age < group.maxAge) return group
  }
  return AGE_GROUP_DEFS[1]
}

/**
 * Which band a score falls into at this age.
 *
 * Both boundaries are EXCLUSIVE, which is how the literature quotes them:
 * "below 1.30" rules out and "above 2.67" rules in, so a score of exactly
 * 1.30 or exactly 2.67 is indeterminate. That is not a rounding artefact to be
 * tidied away: the two cut-offs bound the band that says "this test has not
 * decided", and the values sitting exactly on them are precisely the ones it
 * has not decided about.
 *
 * Pass a value from `roundScore` (see the note there).
 */
export function fib4BandFor(age: number, score: number): Fib4Band {
  const { lowBelow, highAbove } = fib4AgeGroupFor(age)
  if (score > highAbove) return "high"
  if (score < lowBelow) return "low"
  return "indeterminate"
}
